import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import {
    BackToList,
    useAnnouncedSnackbar,
    useMasterDetail,
    workspaceListCls,
    workspaceRootCls,
} from "./_workspace";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    FilterChip as RebaseFilterChip,
    IconButton as RebaseIconButton,
    SearchBar as RebaseSearchBar,
    Separator as RebaseSeparator,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    RefreshCwIcon as RebaseRefreshCwIcon,
    ExternalLinkIcon as RebaseExternalLinkIcon,
    cls,
    cardClickableMixin,
    cardSelectedMixin,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import {
    Archive as RebaseArchiveIcon,
    ArchiveRestore as RebaseUnarchiveIcon,
    BellOff as RebaseBellOffIcon,
    Check as RebaseCheckIcon,
    Download as RebaseDownloadIcon,
    PenLine as RebasePenIcon,
    Reply as RebaseReplyIcon,
    UserRound as RebaseUserIcon
} from "lucide-react";

import { EmailComposer, type ComposerTarget } from "./EmailComposer";
import { EmailThreadView } from "./EmailThreadView";
import { NEW_MAIL_EVENT } from "./useInboxAutoSync";
import { ThreadLinkPicker, type LinkOutcome, type ThreadVacancy } from "./ThreadLinkPicker";
import {
    CONVERSATION_PARAM,
    OPEN_CONVERSATION_EVENT,
    callFunction,
    compareThreads,
    gmailThreadUrl,
    markThreadRead,
    tabFor,
    daysWaiting,
    entityTypeOf,
    ENTITY_COLOR,
    ENTITY_LABEL,
    INBOX_TABS,
    INBOX_TAB_HINT,
    inTab,
    isDueForFollowUp,
    followUpLabel,
    isPersonThread,
    KIND_CHIP,
    KIND_LABEL,
    kindOf,
    sinceLabel,
    threadPerson,
    threadTitle,
    threadWith,
    type InboxTab,
    type MailboxStatus,
    type MessageRow,
    type ThreadRow
} from "./_email_shared";
import { employerFromAddress } from "../employer_identity";
import { plural } from "../plural";
import { OwnerControl, useTeam } from "./_team";
import { EntityLink } from "./_links";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const FilterChip = RebaseFilterChip as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const SearchBar = RebaseSearchBar as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const RefreshCwIcon = RebaseRefreshCwIcon as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const ArchiveIcon = RebaseArchiveIcon as React.ComponentType<any>;
const UnarchiveIcon = RebaseUnarchiveIcon as React.ComponentType<any>;
const DownloadIcon = RebaseDownloadIcon as React.ComponentType<any>;
const ReplyIcon = RebaseReplyIcon as React.ComponentType<any>;
const BellOffIcon = RebaseBellOffIcon as React.ComponentType<any>;
const CheckIcon = RebaseCheckIcon as React.ComponentType<any>;
const PenIcon = RebasePenIcon as React.ComponentType<any>;
const UserIcon = RebaseUserIcon as React.ComponentType<any>;

/**
 * "Correo" — the SustenTalent mailbox, inside the panel.
 *
 * Everything this business does with a person outside the product happens in
 * email, and until this screen existed none of it was here. The panel could say
 * that Acciona had four contacts and that somebody clicked "Escribir" on the
 * 4th; it could not say what was written, whether anyone replied, or what they
 * said. That half lived in one Gmail account, joined to the CRM by memory.
 *
 * ── The tabs are a queue, not a filing system ────────────────────────
 *
 * "Esperan respuesta" leads, and it is the only one that answers the question
 * somebody has when they sit down: what do I owe. It is every conversation whose
 * last message came from them — derived from the messages rather than stored,
 * because a stored copy goes stale the moment somebody replies from Gmail
 * instead of from here.
 *
 * "Sin asignar" is the second queue and a quieter one: threads the linker could
 * not attach to anybody. Assigning one is a person's decision and it sticks —
 * `linked_by = manual`, which ingest will never overwrite. For the common case
 * — a stranger applying by email — assigning means *creating* the candidate,
 * and the picker offers that first.
 *
 * Both queues are conversations with people. A quarter of this mailbox is
 * machines (see email-kind.ts), and those threads live under "Automáticos":
 * kept, readable, counted towards nothing. "Es automático" on a thread the
 * classifier missed moves it there and writes a rule for the next one.
 *
 * ── What this screen does not do ─────────────────────────────────────
 *
 * It does not delete, archive or mark anything as read *in Gmail*. The mailbox
 * belongs to a person who also reads it there, and an integration that reaches
 * into somebody's inbox and changes state is one they turn off. "Cerrar" closes
 * the conversation here and nowhere else.
 */
const THREAD_LIMIT = 500;
const MESSAGE_LIMIT = 200;

export function InboxView() {
    return <Inbox />;
}

function Inbox() {
    const client = useRebaseClient<RebaseClient>();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();
    const { openDetail, closeDetail, masterCls, detailCls } = useMasterDetail();
    const { me, shortName } = useTeam();

    // Identity-stable, so `ThreadCard`'s memo compares equal for every row the
    // selection did not touch. An inline arrow here would allocate a new
    // callback per row per render and the memo would never hit once.
    const selectThread = useCallback((id: string) => {
        setSelectedId(id);
        openDetail();
    }, [openDetail]);

    const [threads, setThreads] = useState<ThreadRow[] | null>(null);
    const [messages, setMessages] = useState<MessageRow[]>([]);
    const [status, setStatus] = useState<MailboxStatus | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [tab, setTab] = useState<InboxTab>("unread");
    const [tabChosen, setTabChosen] = useState(false);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    /**
     * Kept apart from `loading`, which is the thread *list*.
     *
     * On a slow connection the reader used to hold the previous conversation's
     * messages until the new ones arrived, so switching threads changed the
     * heading and nothing else — which reads as a broken screen rather than a
     * slow one.
     */
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [working, setWorking] = useState(false);
    const [replying, setReplying] = useState(false);
    const [assigning, setAssigning] = useState(false);
    /** The "¿Silenciar a …?" panel is open. */
    const [silencing, setSilencing] = useState(false);
    const [error, setError] = useState("");
    /**
     * The vacancies threads point at, by id, and the people they are linked to.
     *
     * Resolved once per load for the threads on screen rather than stored on
     * the thread: a title or a name is somebody else's to change, and a copy
     * on four hundred threads is four hundred stale copies. Both are one
     * `in` query against a short id list.
     */
    const [vacancies, setVacancies] = useState<Map<string, ThreadVacancy>>(new Map());
    const [linkedNames, setLinkedNames] = useState<Map<string, string>>(new Map());

    const data = (client as any)?.data;

    /**
     * The conversation that has to be in the list, however old it is.
     *
     * The list is the newest THREAD_LIMIT, and a link to anything older — a
     * candidate's first exchange, opened from their record — found nothing,
     * fell through, and opened the queue instead. The same rule keeps a reload
     * (new mail, a sync, a reply) from pulling the conversation somebody is
     * reading out from under them once it has aged past the cut. Kept current
     * by an effect below: the linked conversation until it has been shown,
     * the selected one after that.
     */
    const pinnedThread = useRef<string | null>(null);

    const load = useCallback(async () => {
        if (!data) return;
        setLoading(true);
        setError("");
        try {
            const res = await data.collection("email-threads").find({
                orderBy: ["last_message_at", "desc"],
                limit: THREAD_LIMIT
            });
            let rows = (res?.data ?? []) as ThreadRow[];
            const pinned = pinnedThread.current;
            if (pinned && !rows.some(t => t.id === pinned)) {
                const row = await data.collection("email-threads").findById(pinned).catch(() => undefined);
                if (row) rows = [...rows, row as ThreadRow];
            }
            setThreads(rows);
        } catch (err: any) {
            setError(err?.message || "No se pudieron cargar las conversaciones");
            setThreads([]);
        } finally {
            setLoading(false);
        }
    }, [data]);

    /**
     * Titles for the vacancies the loaded threads name, and names for the
     * candidates and companies they are linked to.
     *
     * "Candidato — cambiar" said what kind of thing a thread was about and not
     * who; after creating a candidate from a thread, the first thing the
     * operator wants is the record, and the header is where they look.
     */
    useEffect(() => {
        if (!data || !threads) return;
        const jobIds = [...new Set(threads.map(t => t.job_id).filter((v): v is string => Boolean(v)))];
        const talentIds = [...new Set(threads.filter(t => entityTypeOf(t) === "talent").map(t => t.entity_id).filter((v): v is string => Boolean(v)))];
        const companyIds = [...new Set(threads.filter(t => entityTypeOf(t) === "company").map(t => t.entity_id).filter((v): v is string => Boolean(v)))];
        let live = true;
        void (async () => {
            const [jobs, talents, companies] = await Promise.all([
                jobIds.length
                    ? data.collection("jobs").find({ where: { id: ["in", jobIds] }, limit: jobIds.length }).catch(() => undefined)
                    : undefined,
                talentIds.length
                    ? data.collection("talents").find({ where: { id: ["in", talentIds] }, limit: talentIds.length }).catch(() => undefined)
                    : undefined,
                companyIds.length
                    ? data.collection("companies").find({ where: { id: ["in", companyIds] }, limit: companyIds.length }).catch(() => undefined)
                    : undefined
            ]);
            if (!live) return;
            const byJob = new Map<string, ThreadVacancy>();
            for (const row of (jobs?.data ?? []) as Record<string, any>[]) {
                byJob.set(String(row.id), { id: String(row.id), title: String(row.title ?? "(sin título)"), company_id: row.company_id ?? null });
            }
            setVacancies(byJob);
            const names = new Map<string, string>();
            for (const row of (talents?.data ?? []) as Record<string, any>[]) names.set(String(row.id), String(row.full_name ?? ""));
            for (const row of (companies?.data ?? []) as Record<string, any>[]) names.set(String(row.id), String(row.name ?? ""));
            setLinkedNames(names);
        })();
        return () => { live = false; };
    }, [data, threads]);

    /** Whether the mailbox is even wired up, and when it was last read. */
    const loadStatus = useCallback(async () => {
        try {
            setStatus(await callFunction<MailboxStatus>(client, "sync-inbox"));
        } catch {
            // A panel that cannot reach its own status endpoint still lists the
            // threads it already has; only the banner is missing.
            setStatus(null);
        }
    }, [client]);

    useEffect(() => { void load(); }, [load]);
    useEffect(() => { void loadStatus(); }, [loadStatus]);

    /**
     * Pick up mail the background poll found.
     *
     * The poller lives at the top of the app (see `InboxAutoSync`), so it can
     * read the mailbox on any screen. When it stores something it says so on
     * `window`, and this is the screen that most obviously has to react —
     * otherwise the operator is looking at a list that the app already knows is
     * out of date.
     *
     * Deliberately reloads from the database rather than merging the poll's
     * result: the response says how many messages were stored, not which
     * threads they landed on, and re-reading is one query.
     */
    useEffect(() => {
        const onNewMail = () => {
            void load();
            void loadStatus();
        };
        window.addEventListener(NEW_MAIL_EVENT, onNewMail);
        return () => window.removeEventListener(NEW_MAIL_EVENT, onNewMail);
    }, [load, loadStatus]);

    /**
     * The thread whose messages the state is allowed to hold.
     *
     * Two clicks in quick succession start two requests, and the second thread's
     * messages are not necessarily the second to arrive — a slower query for the
     * thread you just left can land after the one you are now looking at and
     * overwrite it. The symptom is precise and baffling: the heading changes to
     * the new conversation and the messages underneath stay on the old one.
     *
     * A ref rather than state because it must be readable by a callback that
     * closed over an earlier render, and must not itself cause one.
     */
    const wantedThread = useRef<string | null>(null);

    const loadMessages = useCallback(async (threadId: string | null) => {
        wantedThread.current = threadId;
        if (!data || !threadId) {
            setMessages([]);
            setLoadingMessages(false);
            return;
        }
        setLoadingMessages(true);
        try {
            const res = await data.collection("email-messages").find({
                where: { thread_id: ["==", threadId] },
                orderBy: ["sent_at", "asc"],
                limit: MESSAGE_LIMIT
            });
            // Somebody has clicked elsewhere since this was asked for. Dropping
            // the answer is the whole fix: it is not stale data, it is data for
            // a question nobody is asking any more.
            if (wantedThread.current !== threadId) return;
            setMessages(res?.data ?? []);
        } catch {
            if (wantedThread.current === threadId) setMessages([]);
        } finally {
            // Only the request that is still wanted may clear the flag —
            // otherwise an abandoned one turns the spinner off while the
            // conversation you actually chose is still on its way.
            if (wantedThread.current === threadId) setLoadingMessages(false);
        }
    }, [data]);

    const counts = useMemo(() => {
        const list = threads ?? [];
        const count = (tab: InboxTab) => list.filter(t => inTab(t, tab, me)).length;
        return {
            unread: count("unread"),
            waiting: count("waiting"),
            mine: count("mine"),
            today: count("today"),
            unlinked: count("unlinked"),
            all: count("all"),
            automated: count("automated"),
            closed: count("closed")
        } satisfies Record<InboxTab, number>;
    }, [threads, me]);

    /**
     * A conversation somebody arrived here to read.
     *
     * Every conversation opened elsewhere in the panel writes this address
     * while its side panel is open (`conversationUrl`), so it is what a reload,
     * a copied link and the panel's own expand button all arrive with. A link
     * that lands on the inbox and then selects whatever happens to be at the
     * top of the queue is a link that does not work. Read once and then
     * cleared from the URL, so a later reload does not drag the operator back
     * to a conversation they finished with.
     */
    const [wantedFromUrl, setWantedFromUrl] = useState<string | null>(() => {
        if (typeof window === "undefined") return null;
        return new URLSearchParams(window.location.search).get(CONVERSATION_PARAM);
    });

    useEffect(() => {
        pinnedThread.current = wantedFromUrl ?? selectedId;
    }, [wantedFromUrl, selectedId]);

    /**
     * The same, asked for while this screen is already open.
     *
     * The conversation panel's expand button navigates here, and when Correo is
     * the screen *underneath* the panel — you opened a candidate from a thread,
     * then one of their other threads — the route does not change and this
     * component does not remount, so the parameter read above is never read
     * again. The panel says so with an event instead.
     *
     * Re-reads the mailbox rather than looking in the list it has: the
     * conversation may be older than THREAD_LIMIT, and `pinnedThread` is what
     * brings it in. Clearing `threads` first is what lets the first-selection
     * effect below run again on the answer, and nothing else.
     */
    useEffect(() => {
        const onOpen = (event: Event) => {
            const id = (event as CustomEvent<unknown>).detail;
            if (typeof id !== "string" || !id) return;
            pinnedThread.current = id;
            setWantedFromUrl(id);
            setTabChosen(false);
            setThreads(null);
            openDetail();
            void load();
        };
        window.addEventListener(OPEN_CONVERSATION_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_CONVERSATION_EVENT, onOpen);
    }, [load, openDetail]);

    /**
     * A message to somebody this mailbox has no conversation with yet.
     *
     * The inbox could only reply. Starting a thread meant going to the
     * person's record — and an employer who wrote through the site has no
     * record to go to until somebody files them. "Empresas que contratan"
     * links here with `?para=` and `?asunto=` for exactly that reply, and the
     * pen in the header opens the same composer empty.
     */
    const [composeNew, setComposeNew] = useState<ComposerTarget | null>(() => {
        if (typeof window === "undefined") return null;
        const params = new URLSearchParams(window.location.search);
        const to = (params.get("para") ?? "").trim();
        if (!to) return null;
        return { to: [to], subject: params.get("asunto") ?? "" };
    });
    useEffect(() => {
        if (!composeNew || typeof window === "undefined") return;
        const url = new URL(window.location.href);
        if (!url.searchParams.has("para")) return;
        url.searchParams.delete("para");
        url.searchParams.delete("asunto");
        window.history.replaceState({}, "", url.toString());
    }, [composeNew]);

    /**
     * Open on the queue, but only when there is something in it.
     *
     * The same rule "Empresas que contratan" follows: a workspace whose default
     * tab says "nada aquí" reads as an empty product. Decided once, after the
     * first load, so it never fights the operator's own clicks.
     */
    useEffect(() => {
        if (tabChosen || threads === null) return;
        setTabChosen(true);
        // A deep link outranks the queue: the operator asked for one specific
        // conversation, and it is very often already read or already filed,
        // which no queue tab contains. The tab has to be the one that actually
        // holds it, or the "keep the selection inside the tab" effect below
        // moves off it on the next render.
        //
        // That effect is also why it must not run on *this* commit, and now
        // does not (see its `tabChosen` guard). Both effects fire when the
        // list lands, this one first, and the other one still sees the tab
        // and the selection from before — "unread", nothing — so it picked the
        // top of the unread queue after this line had picked the linked
        // conversation. Same setter, last write wins: every link from a
        // candidate's record opened Correo on somebody else's mail.
        const wanted = wantedFromUrl ? threads.find(t => t.id === wantedFromUrl) : undefined;
        if (wanted) {
            setTab(tabFor(wanted));
            setSelectedId(wanted.id);
            // Somebody asked for this conversation, so below `lg` it is the
            // conversation they get, not the list with it highlighted.
            openDetail();
            return;
        }
        // Reminders that came due outrank the rest: somebody chose today.
        if (counts.today > 0) setTab("today");
        else if (counts.unread > 0) setTab("unread");
        else if (counts.waiting > 0) setTab("waiting");
        else setTab("all");
    }, [threads, counts, tabChosen, wantedFromUrl, openDetail]);

    // Cleared once it has been honoured, so the "keep the selection inside the
    // tab" effect below is free to move on when the operator clicks elsewhere.
    useEffect(() => {
        if (!wantedFromUrl || !tabChosen) return;
        setWantedFromUrl(null);
        if (typeof window === "undefined") return;
        const url = new URL(window.location.href);
        if (!url.searchParams.has(CONVERSATION_PARAM)) return;
        url.searchParams.delete(CONVERSATION_PARAM);
        // The router's own entry state is kept: replacing it with `{}` erased
        // the index it uses to tell Back from Forward on this entry.
        window.history.replaceState(window.history.state, "", url.toString());
    }, [tabChosen, wantedFromUrl]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (threads ?? [])
            .filter(t => inTab(t, tab, me))
            .filter(t => !term
                || threadTitle(t).toLowerCase().includes(term)
                || (t.participants ?? []).some(p => p.toLowerCase().includes(term)))
            .sort(tab === "today"
                ? (a, b) => (a.follow_up_at ?? "").localeCompare(b.follow_up_at ?? "")
                : compareThreads);
    }, [threads, tab, search, me]);

    const selected = useMemo(
        () => (threads ?? []).find(t => t.id === selectedId) ?? null,
        [threads, selectedId]
    );

    // Keep the selection on something the current tab contains.
    //
    // Not until the first tab has been chosen: that decision makes the first
    // selection too, and on the commit where it is made this effect is still
    // looking at the tab and the selection from before it. See the effect that
    // chooses the tab.
    useEffect(() => {
        if (!tabChosen) return;
        if (visible.length === 0) {
            setSelectedId(null);
            return;
        }
        if (!selectedId || !visible.some(t => t.id === selectedId)) {
            setSelectedId(visible[0].id);
        }
    }, [tabChosen, visible, selectedId]);

    useEffect(() => {
        setReplying(false);
        setAssigning(false);
        setSilencing(false);
        // Cleared first, so the gap between selecting a conversation and its
        // messages arriving shows nothing rather than the previous one's. An
        // empty pane for 200ms is honest; the wrong conversation under the right
        // heading is not.
        setMessages([]);
        void loadMessages(selected?.id ?? null);
    }, [selected?.id, loadMessages]);

    /**
     * Opening a conversation is reading it.
     *
     * Written straight away rather than after a delay: an inbox where the badge
     * disagrees with what is on screen is one people stop believing, and this
     * table is not so large that one update per open is worth optimising. The
     * local state moves first so the list does not wait for a round trip.
     */
    useEffect(() => {
        if (!data || !selected?.id || !selected.unread) return;
        const id = selected.id;
        setThreads(rows => (rows ?? []).map(t => (t.id === id ? { ...t, unread: false } : t)));

        // The same gesture in Gmail: opening a conversation here marks it read
        // there, and files it under the labels this thread's classification
        // implies. Shared with the conversation panel — see `markThreadRead`.
        markThreadRead(client, id).catch(() => {
            // Put it back: a badge that vanished without the write landing is a
            // conversation nobody will look at again.
            setThreads(rows => (rows ?? []).map(t => (t.id === id ? { ...t, unread: true } : t)));
        });
    }, [client, data, selected?.id, selected?.unread]);

    const sync = useCallback(async () => {
        setSyncing(true);
        setError("");
        try {
            // `force`, because a person clicked. The background poll is
            // throttled by INBOX_MIN_POLL_SECONDS and should be; a button that
            // answered "we checked 30 seconds ago" is a button that does not
            // work, and the operator pressing it is usually waiting for one
            // specific reply.
            const result = await callFunction<{
                fetched: number; stored: number; threads_created: number; truncated: boolean;
                relinked?: number; vacancies_recognised?: number; reclassified?: number;
            }>(client, "sync-inbox", { force: true });
            // `relinked` is reported even when there is no new mail, because it
            // is the answer to a question the operator is often asking with this
            // button: "the person I just added — did their old thread attach?".
            const relinked = [
                result.relinked ? `${result.relinked} ${plural(result.relinked, "conversación conectada", "conversaciones conectadas")}` : "",
                result.vacancies_recognised ? `${result.vacancies_recognised} ${plural(result.vacancies_recognised, "vacante reconocida", "vacantes reconocidas")}` : "",
                result.reclassified ? `${result.reclassified} ${plural(result.reclassified, "movida", "movidas")} a «Correo de máquinas»` : ""
            ].filter(Boolean).join(" · ");
            snackbar.open({
                type: "success",
                message: result.stored === 0
                    ? (relinked || "No hay correo nuevo")
                    : `${result.stored} ${plural(result.stored, "mensaje nuevo", "mensajes nuevos")}` +
                      (result.threads_created ? ` · ${result.threads_created} ${plural(result.threads_created, "conversación nueva", "conversaciones nuevas")}` : "") +
                      (relinked ? ` · ${relinked}` : "") +
                      (result.truncated ? " · queda más por leer" : "")
            });
            await Promise.all([load(), loadStatus()]);
        } catch (err: any) {
            setError(err?.message || "No se pudo leer el buzón");
        } finally {
            setSyncing(false);
        }
    }, [client, load, loadStatus, snackbar]);

    const setThreadStatus = useCallback(async (thread: ThreadRow, next: "open" | "closed") => {
        if (!data) return;
        setWorking(true);
        try {
            await data.collection("email-threads").update(thread.id, { status: next });
            setThreads(rows => (rows ?? []).map(t => (t.id === thread.id ? { ...t, status: next } : t)));
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo cambiar el estado" });
        } finally {
            setWorking(false);
        }
    }, [data, snackbar]);

    /**
     * "Silenciar remitente" / "Es una persona: devolver a la bandeja".
     *
     * Flipping the thread is the visible half. The half that matters is the
     * rule: a sender the classifier missed — the tax office writes from an
     * address with a person's manners — is written to `email_sender_rules`,
     * so the next thread from it is filed right by ingest. `domain` widens the
     * rule to everything at that host, offered only for a corporate domain;
     * a rule for gmail.com would silence half the candidates.
     *
     * Going the other way deletes the rules that would re-file it: "this is a
     * person" is a stronger statement than "everything from here is a robot".
     * The thread's link is left to the next re-link pass, which now considers
     * it because it is `person` again.
     */
    const setThreadKind = useCallback(async (
        thread: ThreadRow,
        next: "person" | "automated",
        scope: "address" | "domain" = "address"
    ) => {
        if (!data) return;
        setWorking(true);
        try {
            const address = ((thread.participants ?? [])[0] ?? "").toLowerCase();
            const domain = address.split("@")[1] ?? "";
            const pattern = scope === "domain" ? domain : address;

            /**
             * One rule per sender, and the newest click wins.
             *
             * `pattern` is unique, so a person rule for an address an
             * automated rule already names — or the reverse — is a refused
             * insert. Whatever rules name this address or its domain are
             * dropped first; the click being made is the operator's current
             * verdict and the earlier one is what it is correcting.
             */
            const replaceRules = async (kind: "automated" | "person", patterns: string[]) => {
                const existing = await data.collection("email-sender-rules").find({
                    where: { pattern: ["in", [address, domain].filter(Boolean)] },
                    limit: 2
                }).catch(() => undefined);
                await Promise.all(((existing?.data ?? []) as { id: string }[]).map(r =>
                    data.collection("email-sender-rules").delete(r.id).catch(() => undefined)));
                for (const p of patterns.filter(Boolean)) {
                    await data.collection("email-sender-rules").create({
                        pattern: p,
                        kind,
                        note: `Correo: "${threadTitle(thread)}"`
                    }).catch((err: any) => {
                        // Already there is fine — it is the rule we wanted.
                        if (!/unique|duplicate|already exists/i.test(String(err?.message ?? ""))) throw err;
                    });
                }
            };

            if (next === "automated") {
                await data.collection("email-threads").update(thread.id, {
                    kind: "automated",
                    // Nobody to be assigned to. Cleared so the record does not
                    // carry a machine's mail, and the Gmail label sync takes
                    // the label off on the next pass.
                    entity_type: "none", entity_id: null, employer_key: null, contact_id: null, application_id: null,
                    linked_by: "manual"
                });
                await replaceRules("automated", [pattern]);
                // Every other thread from the same sender, already stored:
                // the list is in memory, so this costs no query to find them.
                const siblings = (threads ?? []).filter(t =>
                    t.id !== thread.id && isPersonThread(t) && (t.participants ?? []).some(p =>
                        scope === "domain" ? p.toLowerCase().endsWith(`@${domain}`) : p.toLowerCase() === address));
                await Promise.all(siblings.map(t =>
                    data.collection("email-threads").update(t.id, { kind: "automated" }).catch(() => undefined)));
                const moved = new Set([thread.id, ...siblings.map(t => t.id)]);
                setThreads(rows => (rows ?? []).map(t => (moved.has(t.id) ? { ...t, kind: "automated" as const } : t)));
                snackbar.open({
                    type: "success",
                    message: siblings.length
                        ? `Silenciado ${pattern}: esta conversación y ${siblings.length} más pasan a «Correo de máquinas»`
                        : `Silenciado ${pattern}: esta conversación pasa a «Correo de máquinas»`
                });
            } else {
                await data.collection("email-threads").update(thread.id, { kind: "person" });
                // A `person` rule, not merely the absence of an automated one:
                // Gmail's tabs are read on every poll, and a sender Gmail
                // files under Updates would be flipped back in five minutes.
                // The rule is what makes the click stick. See drizzle/0053.
                await replaceRules("person", [address]);
                setThreads(rows => (rows ?? []).map(t => (t.id === thread.id ? { ...t, kind: "person" as const } : t)));
                snackbar.open({ type: "success", message: `${address} vuelve a la bandeja y ya no se silencia` });
            }
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo cambiar" });
        } finally {
            setWorking(false);
        }
    }, [data, threads, snackbar]);

    /** One field on the thread, written and mirrored locally. */
    const patchThread = useCallback(async (thread: ThreadRow, patch: Partial<ThreadRow>) => {
        if (!data) return;
        try {
            await data.collection("email-threads").update(thread.id, patch);
            setThreads(rows => (rows ?? []).map(t => (t.id === thread.id ? { ...t, ...patch } : t)));
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo guardar" });
        }
    }, [data, snackbar]);

    /** A reminder `days` from now, at nine in the morning local time. Null clears it. */
    const remind = useCallback(async (thread: ThreadRow, days: number | null) => {
        if (days === null) {
            await patchThread(thread, { follow_up_at: null });
            snackbar.open({ type: "success", message: "Recordatorio quitado" });
            return;
        }
        const when = new Date();
        when.setDate(when.getDate() + days);
        when.setHours(9, 0, 0, 0);
        await patchThread(thread, { follow_up_at: when.toISOString() });
        snackbar.open({ type: "success", message: days === 1 ? "Volverá mañana" : `Volverá en ${days} días` });
    }, [patchThread, snackbar]);

    const replyTarget = useMemo<ComposerTarget | null>(() => {
        if (!selected) return null;
        const firstInbound = messages.find(m => m.direction === "inbound" && (m.from_name ?? "").trim());
        return {
            to: (selected.participants ?? []).filter(Boolean),
            subject: "",
            threadId: selected.id,
            // For the templates. The From header's display name is the best
            // name this screen has for the person on the other end.
            recipientName: firstInbound?.from_name ?? (selected.entity_id ? linkedNames.get(selected.entity_id) ?? null : null),
            draftContext: {
                thread_id: selected.id,
                entity_type: entityTypeOf(selected) === "none" ? undefined : entityTypeOf(selected),
                entity_id: selected.entity_id ?? undefined,
                employer_key: selected.employer_key ?? undefined,
                contact_id: selected.contact_id ?? undefined
            }
        };
    }, [selected, messages, linkedNames]);

    return (
        <div className={workspaceRootCls}>
            {liveRegion}

            {/* ── Thread list ── */}
            <div className={cls(masterCls, defaultBorderMixin)}>
                <div className="p-4 pb-2 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <Typography variant="subtitle1" component="h1" className="grow">Correo</Typography>
                        <Tooltip title="Escribir a alguien nuevo" asChild>
                            <IconButton size="smallest" aria-label="Escribir un correo nuevo" onClick={() => { setComposeNew({ to: [], subject: "" }); openDetail(); }}>
                                <PenIcon size={iconSize.smallest} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Buscar correo nuevo en el buzón" asChild>
                            <IconButton size="smallest" aria-label="Sincronizar correo" onClick={sync} disabled={syncing} aria-busy={syncing}>
                                <DownloadIcon size={iconSize.smallest} className={cls(syncing && "animate-pulse")} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Recargar" asChild>
                            <IconButton size="smallest" aria-label="Recargar conversaciones" onClick={load} disabled={loading} aria-busy={loading}>
                                <RefreshCwIcon size={iconSize.smallest} className={cls(loading && "animate-spin")} />
                            </IconButton>
                        </Tooltip>
                    </div>

                    <MailboxBanner status={status} />

                    <SearchBar
                        size="small"
                        placeholder="Buscar por asunto o dirección"
                        onTextSearch={(term: string | undefined) => setSearch(term ?? "")}
                    />

                    <div className="flex flex-wrap gap-1.5">
                        {INBOX_TABS.map(t => (
                            <FilterChip key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                                {t.label} · {counts[t.id]}
                            </FilterChip>
                        ))}
                    </div>
                    {/* What the open tab holds. One line, in the place where
                        somebody would be asking. */}
                    <Typography variant="body2" color="secondary">
                        {INBOX_TAB_HINT[tab]}
                    </Typography>
                </div>

                <div className={workspaceListCls}>
                    {error && <Alert color="error" size="small">{error}</Alert>}
                    {threads === null && !error && <CircularProgressCenter />}
                    {threads !== null && visible.length === 0 && !error && (
                        <div className="flex items-center justify-center py-8 px-4 text-center">
                            <Typography variant="label" color="secondary">
                                {tab === "waiting" ? "No debes ninguna respuesta"
                                    : tab === "mine" ? "No tienes ninguna conversación asignada"
                                    : tab === "today" ? "Nada que retomar hoy"
                                    : tab === "unlinked" ? "Todas las conversaciones están conectadas"
                                    : "Nada aquí"}
                            </Typography>
                        </div>
                    )}
                    {visible.map(thread => (
                        <ThreadCard
                            key={thread.id}
                            thread={thread}
                            selected={thread.id === selectedId}
                            onSelect={selectThread}
                            ownerLabel={shortName}
                            // The candidate's or company's name when the
                            // thread is linked, the name they sign their mail
                            // with when it is not, and the bare address when
                            // there is nothing else. Never the subject.
                            who={threadPerson(thread, thread.entity_id ? linkedNames.get(thread.entity_id) : null)}
                        />
                    ))}
                </div>
            </div>

            {/* ── The conversation ── */}
            <div className={detailCls}>
                {composeNew && (
                    <>
                    <BackToList label="Conversaciones" onClick={() => { setComposeNew(null); closeDetail(); }}/>
                    <div className="p-6 max-w-3xl flex flex-col gap-5">
                        <Typography variant="h5" component="h2">Nuevo mensaje</Typography>
                        <EmailComposer
                            client={client}
                            target={composeNew}
                            canSend={status?.sending !== false}
                            onSent={async () => {
                                setComposeNew(null);
                                await load();
                                snackbar.open({ type: "success", message: "Enviado" });
                            }}
                            onCancel={() => setComposeNew(null)}
                        />
                    </div>
                    </>
                )}

                {!composeNew && !selected && (
                    <div className="flex items-center justify-center h-full">
                        <Typography variant="label" color="secondary">Elige una conversación</Typography>
                    </div>
                )}

                {!composeNew && selected && (
                    <>
                    <BackToList label="Conversaciones" onClick={closeDetail}/>
                    <div className="p-6 max-w-3xl flex flex-col gap-5">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-start gap-3 flex-wrap">
                                <Typography variant="h5" component="h2" className="grow">
                                    {threadTitle(selected)}
                                </Typography>
                                {isPersonThread(selected) ? (
                                    <Button
                                        size="small"
                                        variant={assigning ? "filled" : "outlined"}
                                        onClick={() => setAssigning(v => !v)}
                                    >
                                        {entityTypeOf(selected) === "none"
                                            ? "Conectar con un candidato o una empresa"
                                            : `${ENTITY_LABEL[entityTypeOf(selected)]} — cambiar`}
                                    </Button>
                                ) : (
                                    <Chip size="small" colorScheme="grayLighter">{KIND_LABEL[kindOf(selected)]}</Chip>
                                )}
                            </div>
                            <Typography variant="body2" color="secondary">
                                {threadWith(selected)} · {selected.message_count ?? 0} {plural(selected.message_count ?? 0, "mensaje", "mensajes")}
                                {selected.linked_by === "manual" && isPersonThread(selected) && " · conectada a mano"}
                            </Typography>
                            {/* Who will answer, and when to come back to it. */}
                            {isPersonThread(selected) && selected.status !== "closed" && (
                                <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                                    <OwnerControl
                                        owner={selected.owner}
                                        disabled={working}
                                        onChange={next => patchThread(selected, { owner: next })}
                                    />
                                    <span className="inline-flex items-center gap-1.5 text-sm">
                                        <span className="opacity-70">Volver a mirar</span>
                                        {selected.follow_up_at ? (
                                            <>
                                                <Chip size="small" colorScheme={isDueForFollowUp(selected) ? "orangeLighter" : "grayLighter"}>
                                                    {followUpLabel(selected)}
                                                </Chip>
                                                <button type="button" className="text-primary hover:underline" disabled={working} onClick={() => void remind(selected, null)}>quitar</button>
                                            </>
                                        ) : (
                                            <>
                                                <button type="button" className="text-primary hover:underline" disabled={working} onClick={() => void remind(selected, 1)}>mañana</button>
                                                <span className="opacity-40">·</span>
                                                <button type="button" className="text-primary hover:underline" disabled={working} onClick={() => void remind(selected, 3)}>en 3 días</button>
                                                <span className="opacity-40">·</span>
                                                <button type="button" className="text-primary hover:underline" disabled={working} onClick={() => void remind(selected, 7)}>en una semana</button>
                                            </>
                                        )}
                                    </span>
                                </div>
                            )}
                            {/* Who, by name, with the record one click away —
                                the thing "Candidato — cambiar" never said. */}
                            {(entityTypeOf(selected) === "talent" || entityTypeOf(selected) === "company") && selected.entity_id && (
                                <Typography variant="body2">
                                    <UserIcon size={iconSize.smallest} className="inline mr-1 opacity-60" />
                                    <EntityLink kind={entityTypeOf(selected) === "talent" ? "talent" : "company"} id={selected.entity_id}>
                                        {linkedNames.get(selected.entity_id) || (entityTypeOf(selected) === "talent" ? "ver la ficha del candidato" : "ver la ficha de la empresa")}
                                    </EntityLink>
                                </Typography>
                            )}
                            {selected.job_id && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Chip size="small" colorScheme="purpleLighter">
                                        {selected.application_id ? "Candidatura" : "Vacante"}
                                    </Chip>
                                    <Typography variant="body2" color="secondary">
                                        <EntityLink kind="job" id={selected.job_id}>{vacancies.get(selected.job_id)?.title ?? "vacante"}</EntityLink>
                                        {!selected.application_id && " · reconocida en el asunto"}
                                    </Typography>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 flex-wrap items-center">
                            {selected.status === "closed" ? (
                                <Tooltip title="Vuelve a la bandeja del panel" asChild>
                                    <Button size="small" variant="text" disabled={working}
                                        onClick={() => setThreadStatus(selected, "open")}>
                                        <UnarchiveIcon size={iconSize.smallest} />
                                        Sacar del archivo
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Tooltip title="La quita de la bandeja del panel. En Gmail no cambia nada." asChild>
                                    <Button size="small" variant="text" disabled={working}
                                        onClick={() => setThreadStatus(selected, "closed")}>
                                        <ArchiveIcon size={iconSize.smallest} />
                                        Archivar
                                    </Button>
                                </Tooltip>
                            )}
                            {gmailThreadUrl(selected.gmail_thread_id) && (
                                <Button
                                    component="a"
                                    size="small"
                                    variant="text"
                                    href={gmailThreadUrl(selected.gmail_thread_id) as string}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLinkIcon size={iconSize.smallest} />
                                    Abrir en Gmail
                                </Button>
                            )}
                            {/* Whether an answer is owed, said by a person.
                                The queue guesses it from their last message
                                (drizzle/0055) and is sometimes wrong in both
                                directions; this is how it gets corrected, and
                                it is the fastest way to empty the queue of
                                conversations that are simply finished. */}
                            {isPersonThread(selected) && selected.status !== "closed" && (
                                selected.needs_reply === false ? (
                                    <Button size="small" variant="text" disabled={working}
                                        onClick={() => patchThread(selected, { needs_reply: true })}>
                                        <ReplyIcon size={iconSize.smallest} />
                                        Sigue pendiente
                                    </Button>
                                ) : (
                                    <Button size="small" variant="text" disabled={working}
                                        onClick={() => patchThread(selected, { needs_reply: false })}>
                                        <CheckIcon size={iconSize.smallest} />
                                        No hace falta contestar
                                    </Button>
                                )
                            )}
                            {isPersonThread(selected) ? (
                                <Button size="small" variant={silencing ? "filled" : "text"} disabled={working}
                                    onClick={() => setSilencing(v => !v)}>
                                    <BellOffIcon size={iconSize.smallest} />
                                    Silenciar remitente
                                </Button>
                            ) : (
                                <Button size="small" variant="text" disabled={working}
                                    onClick={() => setThreadKind(selected, "person")}>
                                    <UserIcon size={iconSize.smallest} />
                                    Es una persona: devolver a la bandeja
                                </Button>
                            )}
                        </div>

                        {/* Why this conversation is in "Automáticos" and what
                            leaving it there means. The button that put it here
                            said "Es automático" and nothing else, and a button
                            whose effect you have to press it to learn is one
                            people press on a candidate by accident. */}
                        {!isPersonThread(selected) && (
                            <Alert color="info" size="small">
                                Está en «Correo de máquinas»: no cuenta como pendiente, no aparece en las
                                colas y no se puede conectar con un candidato ni con una empresa. Se sigue
                                guardando y leyendo aquí. Devolverla a la bandeja también deja de silenciar
                                a {(selected.participants ?? [])[0] || "este remitente"}.
                            </Alert>
                        )}

                        {/* The confirmation for the button above, spelled out:
                            which address, what stops happening, how much of the
                            mailbox moves, and how to undo it. */}
                        {silencing && isPersonThread(selected) && (() => {
                            const address = ((selected.participants ?? [])[0] ?? "").toLowerCase();
                            const domain = address.split("@")[1] ?? "";
                            // The domain option only for a company's: a rule for
                            // gmail.com would silence half the candidates.
                            const corporate = Boolean(employerFromAddress(address)) && Boolean(domain);
                            const sameAddress = (threads ?? []).filter(t =>
                                t.id !== selected.id && isPersonThread(t)
                                && (t.participants ?? []).some(p => p.toLowerCase() === address)).length;
                            const sameDomain = (threads ?? []).filter(t =>
                                t.id !== selected.id && isPersonThread(t)
                                && (t.participants ?? []).some(p => p.toLowerCase().endsWith(`@${domain}`))).length;
                            return (
                                <Card className="p-4 flex flex-col gap-3">
                                    <Typography variant="subtitle2" component="h3">
                                        ¿Silenciar a {address || "este remitente"}?
                                    </Typography>
                                    <Typography variant="body2" color="secondary">
                                        Sus correos pasan a «Correo de máquinas»: dejan de contar como
                                        pendientes, salen de las colas y ya no se pueden conectar con un
                                        candidato o una empresa. Se siguen guardando y se pueden leer. Los
                                        próximos llegarán ya silenciados, y puedes deshacerlo desde esa
                                        misma pestaña.
                                    </Typography>
                                    <div className="flex gap-2 flex-wrap">
                                        <Button size="small" disabled={working} onClick={async () => {
                                            setSilencing(false);
                                            await setThreadKind(selected, "automated");
                                        }}>
                                            Silenciar {address}
                                            {sameAddress > 0 && ` (y ${sameAddress} ${plural(sameAddress, "conversación más", "conversaciones más")})`}
                                        </Button>
                                        {corporate && (
                                            <Button size="small" variant="outlined" disabled={working} onClick={async () => {
                                                setSilencing(false);
                                                await setThreadKind(selected, "automated", "domain");
                                            }}>
                                                Silenciar todo @{domain}
                                                {sameDomain > 0 && ` (y ${sameDomain} más)`}
                                            </Button>
                                        )}
                                        <Button size="small" variant="text" onClick={() => setSilencing(false)}>
                                            Cancelar
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })()}

                        {assigning && isPersonThread(selected) && (
                            <ThreadLinkPicker
                                thread={selected}
                                messages={messages}
                                vacancy={selected.job_id ? (vacancies.get(selected.job_id) ?? null) : null}
                                client={client}
                                onCancel={() => setAssigning(false)}
                                onLinked={async (outcome: LinkOutcome) => {
                                    setAssigning(false);
                                    await load();
                                    // Push the new classification straight to
                                    // Gmail, so the labels there match what the
                                    // operator just decided rather than waiting
                                    // for the next time somebody opens this.
                                    void callFunction(client, "gmail-sync-thread", { thread_id: selected.id })
                                        .catch(() => { /* labels are not worth an error here */ });
                                    snackbar.open({
                                        type: "success",
                                        message: outcome.createdTalent
                                            ? `Candidato creado: ${outcome.talentName ?? ""}` +
                                              (outcome.applicationId ? " · candidatura registrada" : "")
                                            : outcome.kind === "none"
                                                ? "Desconectada"
                                                : "Conversación conectada" +
                                                  (outcome.applicationId ? " · candidatura registrada" : "")
                                    });
                                }}
                            />
                        )}

                        <Separator />

                        {loadingMessages
                            ? <CircularProgressCenter />
                            : <EmailThreadView messages={messages} client={client} />}

                        {/* Under the conversation, where every mail client
                            puts it: you read to the end and then you write.
                            It used to sit above the messages, so replying
                            meant scrolling past your own empty draft to read
                            what you were answering. */}
                        {replying && replyTarget ? (
                            <EmailComposer
                                client={client}
                                target={replyTarget}
                                canSend={status?.sending !== false}
                                onSent={async () => {
                                    setReplying(false);
                                    await Promise.all([load(), loadMessages(selected.id)]);
                                    snackbar.open({ type: "success", message: "Enviado" });
                                }}
                                onCancel={() => setReplying(false)}
                            />
                        ) : (
                            <div>
                                <Button size="small" variant="outlined" onClick={() => setReplying(true)}>
                                    <ReplyIcon size={iconSize.smallest} />
                                    Responder
                                </Button>
                            </div>
                        )}
                    </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * One row of the list, in the order Gmail puts things in.
 *
 * Who wrote, then when; then the subject; then the first words of the newest
 * message. This screen had the first two swapped — subject above sender — and
 * no preview at all, which is why scanning it meant opening things: eleven
 * rows reading "Especialista Senior en Sostenibilidad y Estándares Mineros"
 * are eleven different people saying eleven different things, and the only
 * part that differs was the part that was not shown.
 *
 * Unread is weight, not a badge, for the same reason it is in every mail
 * client: a bold row is read at a glance and a chip has to be looked at.
 */
const ThreadCard = React.memo(function ThreadCard({
    thread, selected, onSelect, ownerLabel, who
}: {
    thread: ThreadRow; selected: boolean; onSelect: (id: string) => void;
    ownerLabel: (email: string) => string;
    /** The name of whoever is on the other end, when we know it. */
    who: string;
}) {
    const waiting = daysWaiting(thread);
    const preview = (thread.last_snippet ?? "").trim();
    return (
        <Card
            onClick={() => onSelect(thread.id)}
            className={cls("p-3 flex flex-col gap-0.5", cardClickableMixin, selected && cardSelectedMixin)}
        >
            <div className="flex items-baseline gap-2">
                <Typography
                    variant="body2"
                    component="span"
                    className={cls("truncate grow", thread.unread ? "font-bold" : "font-medium")}
                >
                    {who}
                </Typography>
                <Typography variant="body2" color="secondary" className="shrink-0 text-xs">
                    {sinceLabel(thread.last_message_at)}
                </Typography>
            </div>
            <Typography
                variant="body2"
                component="span"
                className={cls("truncate", thread.unread && "font-bold")}
            >
                {threadTitle(thread)}
            </Typography>
            {preview && (
                <Typography variant="body2" color="secondary" className="truncate opacity-70">
                    {preview}
                </Typography>
            )}
            <div className="flex items-center gap-1.5 flex-wrap pt-1">
                {waiting !== null && (
                    // Red past a week, which is the point at which a reply owed
                    // stops being "I'll get to it" and starts being a lost lead.
                    <Chip size="small" colorScheme={waiting >= 7 ? "redLighter" : "orangeLighter"}>
                        {waiting === 0 ? "esperan hoy" : `esperan ${waiting} día${waiting === 1 ? "" : "s"}`}
                    </Chip>
                )}
                {entityTypeOf(thread) !== "none" && (
                    <Chip size="small" colorScheme={ENTITY_COLOR[entityTypeOf(thread)]}>
                        {ENTITY_LABEL[entityTypeOf(thread)]}
                    </Chip>
                )}
                {!isPersonThread(thread) && (
                    <Chip size="small" colorScheme="grayLighter">{KIND_CHIP[kindOf(thread)]}</Chip>
                )}
                {thread.job_id && isPersonThread(thread) && (
                    <Chip size="small" colorScheme="purpleLighter">
                        {thread.application_id ? "candidatura" : "vacante"}
                    </Chip>
                )}
                {thread.follow_up_at && thread.status !== "closed" && (
                    <Chip size="small" colorScheme={isDueForFollowUp(thread) ? "orangeLighter" : "grayLighter"}>
                        {followUpLabel(thread)}
                    </Chip>
                )}
                {thread.owner && (
                    <Chip size="small" colorScheme="grayLighter">{ownerLabel(thread.owner)}</Chip>
                )}
            </div>
        </Card>
    );
});

/**
 * Whether the mailbox is reachable, said out loud.
 *
 * "No new mail" and "we have not been able to reach the mailbox since Tuesday"
 * look identical on an inbox screen, and only one of them is somebody's problem.
 * The banner exists for the second one.
 */
function MailboxBanner({ status }: { status: MailboxStatus | null }) {
    if (!status) return null;

    if (!status.receiving) {
        return (
            <Alert color="warning" size="small">
                Este entorno no lee el buzón: faltan IMAP_HOST, IMAP_USER o IMAP_PASS.
                {status.sending
                    ? " Puedes enviar, pero las respuestas no aparecerán aquí."
                    : " Tampoco está configurado el envío."}
            </Alert>
        );
    }
    if (status.last_error) {
        return (
            <Alert color="error" size="small">
                El último intento de leer {status.mailbox} falló: {status.last_error}
            </Alert>
        );
    }
    return (
        <Typography variant="body2" color="secondary">
            {status.from ?? status.mailbox}
            {/* "leído" read as "somebody read the mail". It is the poll. */}
            {status.last_polled_at
                ? ` · buscamos correo nuevo a las ${sinceLabel(status.last_polled_at)}`
                : " · todavía no hemos mirado el buzón"}
        </Typography>
    );
}
